import React, {useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import Landmark from '@core/scripts/components/landmark-adapter.js';
import {emailValidation} from '@core/scripts/helper/form-input-validation/inputFieldValidations';
import forgotPasswordRequest from './forgot-password-request';
import FriendlyCaptcha from '@core/scripts/components/FriendlyCaptcha.jsx';
import {doSendMetric} from '@core/scripts/helper/send-metric-helper.js';
import ControlledInputField from '@checkout/components/common/forms/fields/ControlledInputField.jsx';
import {Button, Grid, Alert, Dialog, Typography} from 'bonprix-design-system';

export default function ForgotPasswordLayer({
    texts,
    siteKeyForgotPassword,
    isCaptchaEnabled,
    isCaptchaV2Enabled,
    captchaLang,
    onClose,
    onUpdateSuccess,
}) {
    const [globalError, setGlobalError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resetSwitch, setResetSwitch] = useState(false);
    const [captchaStartMode, setCaptchaStartMode] = useState('focus');
    const [captchaCompletedSuccessfully, setCaptchaCompletedSuccessfully] = useState(false);
    const landmarkSent = useRef(false);
    const captchaTokenRef = useRef('');
    const captchaStartRef = useRef(0);

    const defaultValues = {
        email: '',
    };

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: {errors, touchedFields, isDirty, isValid, isSubmitSuccessful},
        trigger,
        control,
    } = useForm({
        mode: 'onChange',
        defaultValues: defaultValues,
    });

    const onSubmit = (values) => {
        setIsLoading(true);
        setGlobalError('');

        const allValues = {
            ...values,
            captchaToken: captchaTokenRef.current,
        };
        forgotPasswordRequest(allValues, texts, handleSuccess, handleError, handleCaptchaReset);
    };

    const handleSuccess = (updatedPageModel) => {
        setIsLoading(false);
        if (updatedPageModel.errors.length > 0) {
            updatedPageModel.errors.forEach((error) => setError('email', {message: texts[error]}));
        } else {
            onUpdateSuccess(updatedPageModel.email);
        }
    };

    const handleCaptchaStart = () => {
        captchaStartRef.current = Date.now();
    };

    const handleCaptchaReset = () => {
        setCaptchaCompletedSuccessfully(false);
        setCaptchaStartMode('auto');
        setResetSwitch(!resetSwitch);
    };

    const handleFirstChange = () => {
        if (!landmarkSent.current) {
            Landmark.sendLandmark({
                landmark: 'action',
                layer_view: 'login_pwvergessen_eingabe',
            });
            landmarkSent.current = true;
        }
    };

    const handleError = () => {
        setIsLoading(false);
        setGlobalError(texts.error_code_empty);
    };

    const handleVerifyCallback = (solution) => {
        captchaTokenRef.current = solution;
        if (solution) {
            doSendMetric('captcha.forgot.password.duration', Date.now() - captchaStartRef.current);
        }
        setCaptchaCompletedSuccessfully(Boolean(solution));
    };

    const handleErrorCallback = (error) => {
        captchaTokenRef.current = 'puzzle-error';
        setCaptchaCompletedSuccessfully(true);
        doSendMetric('captcha.forgot.password.error', 1.0);
    };

    return (
        <Dialog isOpen onClose={onClose}>
            <Dialog.Title>{texts.cot20_customer_lost_password_layer_title}</Dialog.Title>

            <Dialog.Content>
                <div className="pb-safe-area-inset" data-testid="forgot-password-layer">
                    {globalError && (
                        <Alert
                            status="error"
                            showCloseIcon={true}
                            onClose={() => setGlobalError('')}
                        >
                            {globalError}
                        </Alert>
                    )}
                    <FormProvider
                        register={register}
                        formState={{errors, touchedFields, isDirty, isValid, isSubmitSuccessful}}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        trigger={trigger}
                    >
                        <form
                            className="mb-5"
                            aria-label="forgot-password"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Typography variant="body1-bold">{texts.cot20_customer_lost_password_layer_no_problem}</Typography>
                            <Typography>{texts.cot20_customer_lost_password_layer_description_text}</Typography>

                            <div className="mt-6 mb-4">
                                <Typography variant="body1-bold">{texts.cot20_customer_lost_password_layer_email_label}</Typography>
                            </div>
                            <div className="mt-2 l:mt-0 w-full">
                                <Grid>
                                    <Grid.Item span={12} className="l:items-center">
                                        <div className="max-w-sm">
                                            <ControlledInputField
                                                name="email"
                                                type={'email'}
                                                label={texts['cot09_email_address']}
                                                control={control}
                                                validation={emailValidation(texts)}
                                                onChange={handleFirstChange}
                                            />
                                        </div>
                                    </Grid.Item>
                                    <Grid.Item span={12} className="l:items-center">
                                        <div className="max-w-sm mt-2">
                                            <Button
                                                variant={'primary'}
                                                type={'submit'}
                                                disabled={
                                                    !isValid ||
                                                    !isDirty ||
                                                    (isCaptchaEnabled && !captchaCompletedSuccessfully)
                                                }
                                                loading={isLoading}
                                            >
                                                {texts.cot20_customer_lost_password_layer_submit_text}
                                            </Button>
                                        </div>
                                    </Grid.Item>
                                </Grid>
                            </div>

                            {isCaptchaEnabled && (
                                <div className="mt-6">
                                    <FriendlyCaptcha
                                        v2={isCaptchaV2Enabled}
                                        lang={captchaLang}
                                        siteKey={siteKeyForgotPassword}
                                        verifyCallback={handleVerifyCallback}
                                        errorCallback={handleErrorCallback}
                                        startMode={captchaStartMode}
                                        resetSwitch={resetSwitch}
                                        onCaptchaStart={handleCaptchaStart}
                                    />
                                </div>
                            )}
                        </form>
                    </FormProvider>
                </div>
            </Dialog.Content>
        </Dialog>
    );
}
